import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { getToken, setToken } from '@/auth/tokenAccess';
import { getUserManager } from '@/auth/userManager';
import { AUTH_MODE, type AuthMode } from '@/auth/mode';
import { setForbidden } from '@/state/accessSlice';

// Carry fetchBaseQuery's meta (request/response) through the wrapper so endpoints'
// transformResponse can still read headers (e.g. Retry-After) via `meta.response`.
type ReauthBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  NonNullable<unknown>,
  FetchBaseQueryMeta
>;

interface UserManagerLike {
  signinSilent: () => Promise<{ access_token: string } | null>;
  signinRedirect: () => Promise<void>;
}

interface ReauthDeps {
  rawBaseQuery: ReauthBaseQuery;
  getUserManager: () => UserManagerLike;
  mode: AuthMode;
}

export function createReauthBaseQuery(deps: ReauthDeps): ReauthBaseQuery {
  // Shared across concurrent calls so a burst of 401s triggers ONE silent renew
  // (and at most one redirect) instead of hammering the IdP.
  let renewPromise: Promise<{ access_token: string } | null> | null = null;
  let redirecting = false;

  return async (args, api, extraOptions) => {
    let result = await deps.rawBaseQuery(args, api, extraOptions);

    if (result.error?.status === 401 && deps.mode === 'oidc') {
      let renewed = false;
      try {
        renewPromise ??= deps.getUserManager().signinSilent();
        const user = await renewPromise;
        if (user?.access_token) {
          setToken(user.access_token);
          renewed = true;
          result = await deps.rawBaseQuery(args, api, extraOptions);
        }
      } catch {
        // silent renew failed — there is no valid session to refresh from
      } finally {
        renewPromise = null;
      }
      if (result.error?.status === 401) {
        if (renewed) {
          // A freshly-renewed token is STILL rejected (missing required claim,
          // non-JWT/opaque access token, wrong audience, …). Logging in again
          // would mint another token that also fails, so do NOT redirect — that
          // would loop through the IdP. Surface it as a terminal access error.
          api.dispatch(setForbidden());
        } else if (!redirecting) {
          // No session to renew from → genuine re-login.
          redirecting = true;
          await deps.getUserManager().signinRedirect();
        }
      }
    }

    if (result.error?.status === 403) {
      api.dispatch(setForbidden());
    }

    return result;
  };
}

const rawBaseQuery: ReauthBaseQuery = fetchBaseQuery({
  baseUrl: '/api/v1',
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    // AITRAS accepts an optional X-Correlation-Id (≤64 chars) it echoes back for
    // tracing; send a fresh one per request. See docs/API-contract.md.
    headers.set('X-Correlation-Id', crypto.randomUUID());
    return headers;
  },
});

export const baseQueryWithReauth = createReauthBaseQuery({
  rawBaseQuery,
  getUserManager,
  mode: AUTH_MODE,
});
